import React from "react";
import { InfoIcon, ChevronDown } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";

export default function AccountStatsBar({
  plan,
  contract,
  publishedCount = 0,
  totalPublish = 0,
  outstandingCount = 0,
  totalFeatured = 0,
}) {
  const navigate = useNavigate();

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("es-MX", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // Get plan name or default
  const planName = plan?.name || contract?.plan?.name || "";
  const expirationDate = contract?.end_date;
  return (
    <Card className="border-none py-0">
      <div className="flex flex-col md:gap-3 sm:flex-row sm:items-center sm:justify-between p-4 ">
        {/* Plan Info Section */}
        <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center md:gap-6 w-full">
          {/* Plan Status */}
          <div className="flex items-start gap-2 min-w-max">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <div className="cursor-pointer">
                  <InfoIcon className="h-5 w-5 text-zinc-600 mt-0.5 flex-shrink-0" />
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-80 p-4" align="start">
                <div className="flex flex-col space-y-2">
                  <div className="flex items-center justify-between pb-2 border-b">
                    <h4 className="font-semibold text-lg underline">
                      Plan {planName}
                    </h4>
                  </div>
                  <ul className="list-disc pl-4 space-y-1 text-sm text-gray-700">
                    <li>
                      Publica{" "}
                      {plan?.plan_addons?.properties_quantity == -1
                        ? "ilimitadas"
                        : plan?.plan_addons?.properties_quantity}{" "}
                      propiedades
                    </li>
                    <li>
                      {plan?.plan_addons?.properties_prominent} propiedades
                      destacadas
                    </li>
                    <li>
                      {plan?.plan_addons?.client_promoters === -1
                        ? "Usuarios ilimitados"
                        : `${plan?.plan_addons?.client_promoters} usuarios administradores`}
                    </li>
                    <li>Alta visibilidad en los resultados de búsqueda</li>
                    <li>Edita tu publicación sin límites</li>
                    <li>Sube imágenes ilimitadas y videos de tu propiedad</li>
                    {plan?.plan_addons?.client_reports && (
                      <>
                        <li>Acceso a reportes de tus publicaciones</li>
                      </>
                    )}
                  </ul>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
            <div className="flex flex-col gap-1">
              <span className="text-xs text-zinc-600">
                Activo hasta: {formatDate(expirationDate)}
              </span>
              <span className="text-lg font-bold text-zinc-900">
                {planName}
              </span>
            </div>
          </div>
          <div className="flex gap-3 justify-between md:justify-evenly w-full px-4">
            {/* Publications */}
            <div className="flex flex-col">
              <span className="text-xs text-zinc-600">Publicaciones</span>
              <span className="text-lg font-bold text-zinc-900">
                {publishedCount} de {totalPublish == -1 ? "∞" : totalPublish}
              </span>
            </div>

            {/* Featured */}
            <div className="flex flex-col">
              <span className="text-xs text-zinc-600">Destacados</span>
              <span className="text-lg font-bold text-zinc-900">
                {outstandingCount} de{" "}
                {totalFeatured == -1 ? "∞" : totalFeatured}
              </span>
            </div>
          </div>
        </div>

        {/* Action Buttons Section */}
        <div className="flex gap-2 flex-wrap justify-end sm:flex-nowrap w-full md:max-w-[200px]">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1">
                Ver Más
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => navigate("/subscription")}>
                Detalles del plan
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/receipts")}>
                Historial de pagos
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </Card>
  );
}
AccountStatsBar.propTypes = {
  plan: PropTypes.object,
  contract: PropTypes.object,
  publishedCount: PropTypes.number,
  totalPublish: PropTypes.number,
  outstandingCount: PropTypes.number,
  totalFeatured: PropTypes.number,
};
